import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

// v0.4 Task 2: sibling context to ChatDagRuntimeContext. Owns the
// per-node measured-dimensions map so rich variants can self-measure
// and layoutChatDag can read measured sizes (via resolveDims in
// Task 8) without every node spinning up its own ResizeObserver.
//
// Design constraints (architect A1 + A2 + shared-resource-refcounts):
//   - useReducer, not useState, for the map — concurrent setDim calls
//     from multiple RO firings can't clobber each other.
//   - Singleton ResizeObserver shared across every node instance.
//   - RAF-batched dispatch — RO callbacks coalesce into one dispatch
//     per frame so a 5-peer fan arrival doesn't cause 5 renders.
//   - DIM_EPSILON_PX at the reducer write-path, not at the layout
//     read-path — bounds oscillation cheapest where the writes happen.

export interface NodeDims {
  w: number;
  h: number;
}

type DimAction =
  | { type: 'set'; id: string; dims: NodeDims }
  | { type: 'remove'; id: string };

export const DIM_EPSILON_PX = 4;

function dimsReducer(
  state: Map<string, NodeDims>,
  action: DimAction,
): Map<string, NodeDims> {
  switch (action.type) {
    case 'set': {
      const existing = state.get(action.id);
      if (
        existing &&
        Math.abs(existing.w - action.dims.w) < DIM_EPSILON_PX &&
        Math.abs(existing.h - action.dims.h) < DIM_EPSILON_PX
      ) {
        return state;
      }
      const next = new Map(state);
      next.set(action.id, action.dims);
      return next;
    }
    case 'remove': {
      if (!state.has(action.id)) return state;
      const next = new Map(state);
      next.delete(action.id);
      return next;
    }
  }
}

export interface ChatDagDimensionsValue {
  dimensions: Map<string, NodeDims>;
  setDim: (id: string, dims: NodeDims) => void;
  clearDim: (id: string) => void;
  observe: (id: string, el: HTMLElement | null) => void;
  unobserve: (id: string, el: HTMLElement | null) => void;
}

const noop = () => {
  /* default no-op for consumers rendered outside the provider */
};
const DEFAULT_VALUE: ChatDagDimensionsValue = {
  dimensions: new Map(),
  setDim: noop,
  clearDim: noop,
  observe: noop,
  unobserve: noop,
};
const ChatDagDimensionsContext =
  createContext<ChatDagDimensionsValue>(DEFAULT_VALUE);

export const ChatDagDimensionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [dimensions, dispatch] = useReducer(
    dimsReducer,
    new Map<string, NodeDims>(),
  );
  const pendingRef = useRef<Map<string, NodeDims>>(new Map());
  const rafIdRef = useRef<number | null>(null);
  const elementToIdRef = useRef<Map<HTMLElement, string>>(new Map());
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      // jsdom lacks RO; silent no-op keeps tests and SSR paths quiet.
      return;
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const id = elementToIdRef.current.get(entry.target as HTMLElement);
        if (!id) continue;
        const cr = entry.contentRect;
        pendingRef.current.set(id, { w: cr.width, h: cr.height });
      }
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const pending = pendingRef.current;
          pendingRef.current = new Map();
          for (const [id, dims] of pending.entries()) {
            dispatch({ type: 'set', id, dims });
          }
        });
      }
    });
    observerRef.current = ro;
    return () => {
      ro.disconnect();
      observerRef.current = null;
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
      pendingRef.current.clear();
      elementToIdRef.current.clear();
    };
  }, []);

  const setDim = useCallback((id: string, dims: NodeDims) => {
    dispatch({ type: 'set', id, dims });
  }, []);
  const clearDim = useCallback((id: string) => {
    // Drop any RAF-batched pending write before dispatching remove —
    // otherwise a post-unmount flush would resurrect the id.
    pendingRef.current.delete(id);
    dispatch({ type: 'remove', id });
  }, []);
  const observe = useCallback((id: string, el: HTMLElement | null) => {
    if (!observerRef.current || !el) return;
    elementToIdRef.current.set(el, id);
    observerRef.current.observe(el);
  }, []);
  const unobserve = useCallback((id: string, el: HTMLElement | null) => {
    if (!observerRef.current || !el) return;
    observerRef.current.unobserve(el);
    elementToIdRef.current.delete(el);
  }, []);

  const value = useMemo<ChatDagDimensionsValue>(
    () => ({ dimensions, setDim, clearDim, observe, unobserve }),
    [dimensions, setDim, clearDim, observe, unobserve],
  );

  return (
    <ChatDagDimensionsContext.Provider value={value}>
      {children}
    </ChatDagDimensionsContext.Provider>
  );
};

export function useChatDagDimensions(): ChatDagDimensionsValue {
  return useContext(ChatDagDimensionsContext);
}
